import React, { useState, useEffect } from 'react';
import { Search, Plus, Trash2, RefreshCw, ExternalLink, Globe, MessageSquare, Database, AlertCircle, Star, Sparkles, Zap, Award } from 'lucide-react';

// Helper function to get relevance color and styling based on score
const getRelevanceStyle = (score) => {
  if (score >= 0.8) {
    return {
      bgColor: 'bg-gradient-to-r from-emerald-500 to-green-500',
      textColor: 'text-emerald-700',
      bgLight: 'bg-emerald-50',
      borderColor: 'border-emerald-300',
      label: 'Excellent',
      icon: Sparkles,
      ringColor: 'ring-emerald-400'
    };
  } else if (score >= 0.6) {
    return {
      bgColor: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      textColor: 'text-blue-700',
      bgLight: 'bg-blue-50',
      borderColor: 'border-blue-300',
      label: 'Good',
      icon: Star,
      ringColor: 'ring-blue-400'
    };
  } else if (score >= 0.4) {
    return {
      bgColor: 'bg-gradient-to-r from-amber-500 to-yellow-500',
      textColor: 'text-amber-700',
      bgLight: 'bg-amber-50',
      borderColor: 'border-amber-300',
      label: 'Fair',
      icon: Zap,
      ringColor: 'ring-amber-400'
    };
  } else {
    return {
      bgColor: 'bg-gradient-to-r from-orange-500 to-red-400',
      textColor: 'text-orange-700',
      bgLight: 'bg-orange-50',
      borderColor: 'border-orange-300',
      label: 'Low',
      icon: Award,
      ringColor: 'ring-orange-400'
    };
  }
};

// Relevance badge component with gamification
const RelevanceBadge = ({ score }) => {
  const style = getRelevanceStyle(score);
  const IconComponent = style.icon;
  const percentage = Math.round(score * 100);

  return (
    <div className="flex items-center gap-2">
      {/* Circular progress indicator */}
      <div className={`relative w-12 h-12 rounded-full ${style.bgLight} border-2 ${style.borderColor} flex items-center justify-center ring-2 ${style.ringColor} ring-opacity-30`}>
        <div
          className={`absolute inset-1 rounded-full ${style.bgColor} opacity-20`}
          style={{
            background: `conic-gradient(currentColor ${percentage}%, transparent ${percentage}%)`,
          }}
        />
        <span className={`text-xs font-bold ${style.textColor}`}>{percentage}%</span>
      </div>

      {/* Label and icon */}
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          <IconComponent className={`w-3 h-3 ${style.textColor}`} />
          <span className={`text-xs font-semibold ${style.textColor}`}>{style.label}</span>
        </div>
        <span className="text-[10px] text-gray-500">Relevance</span>
      </div>
    </div>
  );
};

// Progress bar component for relevance visualization
const RelevanceBar = ({ score }) => {
  const style = getRelevanceStyle(score);
  const percentage = Math.round(score * 100);

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-gray-500">Relevance</span>
        <span className={`text-xs font-medium ${style.textColor}`}>{percentage}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${style.bgColor} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default function RAGApplication() {
  const [urls, setUrls] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [scanInterval, setScanInterval] = useState(60);
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('query');

  // Use environment variable or fallback to relative path for production
  const API_BASE = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:8000');

  useEffect(() => {
    fetchUrls();
    fetchStats();
    const interval = setInterval(fetchUrls, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchUrls = async () => {
    try {
      const response = await fetch(`${API_BASE}/urls`);
      const data = await response.json();
      setUrls(data);
    } catch (error) {
      console.error('Error fetching URLs:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const addUrl = async (e) => {
    e.preventDefault();
    if (!newUrl) return;

    try {
      const response = await fetch(`${API_BASE}/urls/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: newUrl,
          scan_interval_minutes: scanInterval
        })
      });

      if (response.ok) {
        setNewUrl('');
        setScanInterval(60);
        fetchUrls();
        fetchStats();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      alert('Failed to add URL. Make sure the backend is running.');
    }
  };

  const removeUrl = async (url) => {
    if (!window.confirm(`Remove ${url}?`)) return;

    try {
      await fetch(`${API_BASE}/urls/remove?url=${encodeURIComponent(url)}`, {
        method: 'DELETE'
      });
      fetchUrls();
      fetchStats();
    } catch (error) {
      alert('Failed to remove URL');
    }
  };

  const refreshUrl = async (url) => {
    try {
      await fetch(`${API_BASE}/urls/refresh?url=${encodeURIComponent(url)}`, {
        method: 'POST'
      });
      fetchUrls();
      alert('Refresh triggered!');
    } catch (error) {
      alert('Failed to refresh URL');
    }
  };

  const handleQuery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setAnswer(null);

    try {
      const response = await fetch(`${API_BASE}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: query, top_k: 5 })
      });

      const data = await response.json();
      setAnswer(data);
    } catch (error) {
      alert('Query failed. Make sure the backend is running and Ollama is installed.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Globe className="text-blue-600" />
            Konetou by R-Mad Ltd
          </h1>
          <p className="text-gray-600">Intelligent RAG system - Scan URLs, store knowledge, and query with AI</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total URLs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_urls}</p>
                </div>
                <Globe className="text-blue-500 w-8 h-8" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Chunks</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_chunks}</p>
                </div>
                <Database className="text-purple-500 w-8 h-8" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Sources</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.urls_by_status?.success || 0}
                  </p>
                </div>
                <MessageSquare className="text-green-500 w-8 h-8" />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('query')}
              className={`flex-1 px-6 py-3 font-medium transition-colors ${
                activeTab === 'query'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Search className="inline w-4 h-4 mr-2" />
              Query Knowledge
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`flex-1 px-6 py-3 font-medium transition-colors ${
                activeTab === 'manage'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Globe className="inline w-4 h-4 mr-2" />
              Manage URLs
            </button>
          </div>

          <div className="p-6">
            {/* Query Tab */}
            {activeTab === 'query' && (
              <div>
                <form onSubmit={handleQuery} className="mb-6">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Ask a question about your indexed content..."
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="submit"
                      disabled={loading || !query.trim()}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Thinking...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4" />
                          Search
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Answer Display */}
                {answer && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                        Answer
                      </h3>
                      <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                        {answer.answer}
                      </p>
                    </div>

                    {answer.sources && answer.sources.length > 0 && (
                      <div className="bg-white rounded-lg p-6 border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <ExternalLink className="w-5 h-5 text-gray-600" />
                          Sources & Relevance
                          {answer.relevance_scores && (
                            <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                              {answer.sources.length} references
                            </span>
                          )}
                        </h3>

                        {/* Relevance Legend */}
                        <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <span className="text-xs text-gray-500 font-medium">Relevance Guide:</span>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-500"></div>
                            <span className="text-xs text-gray-600">80%+ Excellent</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                            <span className="text-xs text-gray-600">60-79% Good</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500"></div>
                            <span className="text-xs text-gray-600">40-59% Fair</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-orange-500 to-red-400"></div>
                            <span className="text-xs text-gray-600">&lt;40% Low</span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {answer.sources.map((source, idx) => {
                            const relevanceScore = answer.relevance_scores?.[idx] ?? 0;
                            const style = getRelevanceStyle(relevanceScore);

                            return (
                              <div
                                key={idx}
                                className={`relative p-4 rounded-xl border-2 ${style.borderColor} ${style.bgLight} hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5`}
                              >
                                {/* Rank indicator */}
                                <div className="absolute -top-3 -left-3 w-7 h-7 bg-white rounded-full border-2 border-gray-300 flex items-center justify-center text-xs font-bold text-gray-600 shadow-sm z-10">
                                  #{idx + 1}
                                </div>

                                <div className="flex items-start gap-4">
                                  {/* Relevance Badge */}
                                  <div className="flex-shrink-0">
                                    <RelevanceBadge score={relevanceScore} />
                                  </div>

                                  {/* Source Content */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-semibold text-gray-900 truncate pr-2">
                                        {source.title}
                                      </span>
                                      <a
                                        href={source.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex-shrink-0 p-2 rounded-lg ${style.bgLight} hover:bg-opacity-80 transition-colors`}
                                        title="Open source"
                                      >
                                        <ExternalLink className={`w-4 h-4 ${style.textColor}`} />
                                      </a>
                                    </div>

                                    <a
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all"
                                    >
                                      {source.url}
                                    </a>

                                    {/* Relevance Progress Bar */}
                                    <div className="mt-3">
                                      <RelevanceBar score={relevanceScore} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Overall Match Quality Summary */}
                        {answer.relevance_scores && answer.relevance_scores.length > 0 && (
                          <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Award className="w-5 h-5 text-purple-600" />
                                <span className="text-sm font-medium text-gray-700">Average Relevance Score</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const avgScore = answer.relevance_scores.reduce((a, b) => a + b, 0) / answer.relevance_scores.length;
                                  const avgStyle = getRelevanceStyle(avgScore);
                                  return (
                                    <>
                                      <span className={`text-lg font-bold ${avgStyle.textColor}`}>
                                        {Math.round(avgScore * 100)}%
                                      </span>
                                      <span className={`text-xs px-2 py-1 rounded-full ${avgStyle.bgLight} ${avgStyle.textColor} font-medium`}>
                                        {avgStyle.label}
                                      </span>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {urls.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-lg">No URLs indexed yet</p>
                    <p className="text-sm">Add some URLs in the "Manage URLs" tab to get started</p>
                  </div>
                )}
              </div>
            )}

            {/* Manage URLs Tab */}
            {activeTab === 'manage' && (
              <div>
                {/* Add URL Form */}
                <form onSubmit={addUrl} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Add New URL</h3>
                  <div className="flex flex-col md:flex-row gap-3">
                    <input
                      type="url"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      placeholder="https://example.com"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600 whitespace-nowrap">
                        Scan every
                      </label>
                      <input
                        type="number"
                        value={scanInterval}
                        onChange={(e) => setScanInterval(parseInt(e.target.value))}
                        min="1"
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <span className="text-sm text-gray-600">min</span>
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add URL
                    </button>
                  </div>
                </form>

                {/* URL List */}
                <div className="space-y-3">
                  {urls.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Globe className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-lg">No URLs configured</p>
                      <p className="text-sm">Add your first URL above to start indexing content</p>
                    </div>
                  ) : (
                    urls.map((urlData) => (
                      <div
                        key={urlData.url}
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <a
                              href={urlData.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-blue-600 hover:underline truncate"
                            >
                              {urlData.url}
                            </a>
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(urlData.status)}`}>
                              {urlData.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>{urlData.chunks_count} chunks</span>
                            {urlData.last_crawled && (
                              <span>
                                Last crawled: {new Date(urlData.last_crawled).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => refreshUrl(urlData.url)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Refresh now"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeUrl(urlData.url)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove URL"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Konetou by R-Mad Ltd • Intelligent RAG System</p>
          <p className="mt-1">Powered by ChromaDB + Sentence Transformers + OpenAI/Ollama</p>
        </div>
      </div>
    </div>
  );
}